import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useRouteUnmountEffect, withRouter, resolveRoute} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {persistenceStates, efServiceCaseTypes, apmContractPartnerContractStatusTypes} from '@ace-de/eua-entity-types';
import {useArcGISMap, createMarkerGraphic, markerPickupRouteAPNG, markerPickupRouteBPNG, createLocationRouteSymbol, createFeature, markerTowingActivePNG} from '@ace-de/eua-arcgis-map';
import {useStyles, ContentBlock, ContentItem, Panel, ScrollableBlock, RadioButtonGroup, SearchBox, ToggleSwitch, LoaderSpinner} from '@ace-de/ui-components';
import ContractPartnerItem from './ui-elements/ContractPartnerItem';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import * as sapActionTypes from './sapActionTypes';
import * as serviceAssignmentActionTypes from '../service-assignments/serviceAssignmentActionTypes';
import config from '../config';
import serviceAssignmentPartnerDisabledStateStatuses from '../service-assignments/serviceAssignmentPartnerDisabledStateStatuses';
import routePaths from '../routePaths';
import sapScreenTabs from './sapScreenTabs';

const SAPServiceProviderTab = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand} = useTranslate();
    const {pickupLocationToTowingDestinationRoute, searchSAPContractPartners, submitSAPContractPartnerForm} = props;
    const translateTab = createTranslateShorthand('sap_service_provider_tab');
    const {serviceCase, serviceAssignment, initiateServiceAssignmentACEPartnerDeletionFlow} = props;
    const {initiateServiceAssignmentQMFeedbackDraftCreationFlow, history, contractPartners} = props;
    const {setActiveContractPartnerRecommendationsSnapshot, contractPartnerRecommendationsPersistenceState} = props;
    const arcGISMap = useArcGISMap('service-assignment-pickup');
    const [selectedContractPartnerId, setSelectedContractPartnerId] = useState('');
    const [contractPartnerSearchQuery, setContractPartnerSearchQuery] = useState('');
    const [isVBASearchActive, setIsVBASearchActive] = useState(serviceAssignment?.acePartner?.id && contractPartners
        /* eslint-disable-next-line max-len */
        && contractPartners[serviceAssignment.acePartner.id]?.contractStatus === apmContractPartnerContractStatusTypes.FRIENDLY_TOWING_COMPANY);

    useEffect(() => {
        if (serviceAssignment) {
            const {acePartner: currentContractPartner} = serviceAssignment;
            if (!currentContractPartner || selectedContractPartnerId === currentContractPartner.id) return;

            setSelectedContractPartnerId(currentContractPartner.id);
        }
    }, [serviceAssignment, selectedContractPartnerId]);

    useEffect(() => {
        if (!arcGISMap) return;

        if (!serviceAssignment) return;
        const {destination, pickupLocation} = serviceAssignment;

        arcGISMap.setGraphics({
            graphics: [
                ...(pickupLocation ? [
                    createMarkerGraphic({
                        id: 'pickupLocation',
                        longitude: pickupLocation.longitude,
                        latitude: pickupLocation.latitude,
                        icon: markerPickupRouteAPNG,
                    }),
                ] : []),
                ...(destination ? [
                    createMarkerGraphic({
                        id: 'towingDestination',
                        longitude: destination.longitude,
                        latitude: destination.latitude,
                        icon: markerPickupRouteBPNG,
                    }),
                ] : []),
                ...(pickupLocationToTowingDestinationRoute?.geometry
                    && pickupLocationToTowingDestinationRoute.geometry.type === 'polyline' ? [
                        createFeature({
                            geometry: pickupLocationToTowingDestinationRoute.geometry,
                            symbol: createLocationRouteSymbol(true, true),
                            attributes: {
                                name: 'pickupToTowingRoute',
                            },
                        }),
                    ] : []),
                ...(serviceAssignment.acePartner?.isManuallyAdded ? [
                    createMarkerGraphic({
                        id: 'manuallyAddedACEPartner',
                        longitude: serviceAssignment.acePartner.location?.longitude,
                        latitude: serviceAssignment.acePartner.location?.latitude,
                        icon: markerTowingActivePNG,
                    }),
                ] : []),
                ...(serviceAssignment.acePartner?.isManuallyAdded
                    && serviceAssignment.acePartner?.routeToDamageLocation?.geometry
                    ? [
                        createMarkerGraphic({
                            id: 'manuallyAddedACEPartnerRoute',
                            geometry: serviceAssignment.acePartner.routeToDamageLocation.geometry,
                            isRouteSymbol: true,
                            isActive: true,
                        }),
                    ] : []),
            ],
        });
    }, [arcGISMap, pickupLocationToTowingDestinationRoute, serviceAssignment]);

    const handleDeleteManuallyAddedContractPartner = () => {
        initiateServiceAssignmentACEPartnerDeletionFlow({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData: {
                // remove manually added ACE partner and select the recommended one if exist
                acePartner: recommendedContractPartner || null,
            },
            assignmentType: serviceAssignment.assignmentType,
            isPartnerChangingAllowed: !(caseType === efServiceCaseTypes.VEHICLE
                && serviceAssignmentPartnerDisabledStateStatuses.includes(serviceAssignment.assignmentStatus)),
        });
    };

    const handleContractPartnerSelect = newContractPartnerId => {
        if (!newContractPartnerId || selectedContractPartnerId === newContractPartnerId) return;

        setSelectedContractPartnerId(newContractPartnerId);
        const {contractPartnerRecommendations} = serviceAssignment;
        const selectedPartner = contractPartnerRecommendations
            .find(contractPartner => contractPartner.id === newContractPartnerId);
        const selectedACEPartner = contractPartners[newContractPartnerId] || null;

        submitSAPContractPartnerForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData: {
                acePartner: {
                    ...selectedPartner,
                    ...(selectedPartner.businessContactDetails
                        ? {
                            businessContactDetails: {
                                ...selectedPartner.businessContactDetails,
                                email: selectedACEPartner?.businessContactDetails?.email || '',
                                faxNo: selectedACEPartner?.businessContactDetails?.faxNo || '',
                                phoneNo: selectedACEPartner?.emergencyContacts?.find(contact => contact.is24h7Emergency)?.phoneNo || '',
                            },
                        } : {}
                    ),
                    assignmentChannels: selectedACEPartner?.assignmentChannels || [],
                    assignmentEmail: selectedACEPartner?.assignmentEmail || '',
                    assignment2ndEmail: selectedACEPartner?.assignment2ndEmail || '',
                    assignmentFaxNo: selectedACEPartner?.assignmentFaxNo || '',
                },
                tariffGroup: '',
                routeDistance: 0,
            },
        });
    };

    const handleSearchQueryChange = value => {
        if (!value) {
            searchSAPContractPartners({
                serviceCaseId: serviceAssignment.serviceCaseId,
                serviceAssignmentLineNo: serviceAssignment.lineNo,
                searchQuery: null,
                isVBASearch: isVBASearchActive,
            });
        }
        setContractPartnerSearchQuery(value);
    };

    const handleContractPartnerSearch = () => {
        if (contractPartnerSearchQuery.length > config.MINIMUM_CP_SEARCH_QUERY_LENGTH) {
            searchSAPContractPartners({
                serviceCaseId: serviceAssignment.serviceCaseId,
                serviceAssignmentLineNo: serviceAssignment.lineNo,
                searchQuery: contractPartnerSearchQuery,
                isVBASearch: isVBASearchActive,
            });
        }
    };

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (serviceAssignment?.acePartner?.isManuallyAdded) {
            completeRouteUnmountSideEffect({
                caller: serviceAssignmentActionTypes.INITIATE_QM_FEEDBACK_DRAFT_CREATION_FLOW,
            });
            return;
        }

        // do not create a draft placeholder when an agent switches to a VBA search,
        // since we are not creating generic QM notes when the VBA partner rejects
        if (isVBASearchActive) {
            initiateServiceAssignmentQMFeedbackDraftCreationFlow({
                isDraftPlaceholderDeletion: true,
                qmFeedbackDraftData: {
                    caseId: serviceAssignment.serviceCaseId,
                    lineNo: serviceAssignment.lineNo,
                },
            });
            completeRouteUnmountSideEffect({
                caller: serviceAssignmentActionTypes.INITIATE_QM_FEEDBACK_DRAFT_CREATION_FLOW,
            });
            return;
        }
        // on tab change, if no qm draft is created, create new qm draft which will act as qm placeholder
        // and will be deleted on status accepted
        initiateServiceAssignmentQMFeedbackDraftCreationFlow({
            qmFeedbackDraftData: {
                contractPartnerId: selectedContractPartnerId || serviceAssignment.recommendedContractPartnerId,
                caseId: serviceAssignment.serviceCaseId,
                lineNo: serviceAssignment.lineNo,
                contractPartnerRecommendations: serviceAssignment.contractPartnerRecommendations
                    .map(contractPartner => ({
                        id: contractPartner.id,
                        rank: contractPartner.rank,
                        routeToDamageLocation: {
                            totalKilometers: contractPartner.routeToDamageLocation?.totalKilometers,
                            totalTravelTime: contractPartner.routeToDamageLocation?.totalTravelTime,
                        },
                        formattedAddress: contractPartner.location?.formattedAddress,
                    })),
            },
        });
    }, [
        initiateServiceAssignmentQMFeedbackDraftCreationFlow,
        serviceAssignment,
        selectedContractPartnerId,
        contractPartners,
        isVBASearchActive,
    ]);

    const handleVBAContractPartnerSearch = value => {
        setIsVBASearchActive(!!value);
        // remove the existing query parameters and apply new
        const queryParams = new URLSearchParams();
        queryParams.delete('activeTab');
        queryParams.append('activeTab', sapScreenTabs.SERVICE_PROVIDER);
        queryParams.delete('isVBASearch');
        queryParams.append('isVBASearch', !!value);
        // when switching from the VBA search to a default one, delete QM draft if exists
        if (!value) {
            initiateServiceAssignmentQMFeedbackDraftCreationFlow({
                isDraftDeletion: true,
                qmFeedbackDraftData: {
                    caseId: serviceAssignment.serviceCaseId,
                    lineNo: serviceAssignment.lineNo,
                },
            });
        }
        // set/reset activeContractPartnerRecommendationsSnapshot data
        setActiveContractPartnerRecommendationsSnapshot({
            contractPartnerRecommendations: value
                ? serviceAssignment.contractPartnerRecommendations : [],
            serviceAssignmentId: `${serviceAssignment.serviceCaseId}-${serviceAssignment.lineNo}`,
        });
        history.replace(resolveRoute(
            routePaths.SERVICE_ASSIGNMENT_PICKUP,
            {serviceCaseId: serviceCase.id, serviceAssignmentLineNo: serviceAssignment.lineNo},
            {search: queryParams.toString()},
        ));
    };

    // if no case or assignment don't render
    if (!serviceCase || !serviceAssignment) return null;

    const {contractPartnerRecommendations, recommendedContractPartnerId, assignmentStatus} = serviceAssignment;
    const {caseType} = serviceCase;
    const recommendedContractPartner = contractPartnerRecommendations
        .find(contractPartner => contractPartner.id === recommendedContractPartnerId);

    const isPartnerChangingDisabled = !!serviceAssignment?.acePartner?.isManuallyAdded
        || serviceAssignmentPartnerDisabledStateStatuses.includes(assignmentStatus);
    const isVBASearchParam = (new URLSearchParams(history?.location?.search || '')).get('isVBASearch');
    const isSearchInProgress = contractPartnerRecommendationsPersistenceState === persistenceStates.PENDING;

    return (
        <ContentBlock
            className={cx([
                'global!ace-u-height--full',
                'global!ace-u-max-height--full',
                'global!ace-u-flex--align-stretch',
                'ace-c-content-block--sidebar',
            ])}
        >
            <ContentItem
                className={cx([
                    'ace-c-content-item--span-3',
                    'global!ace-u-height--full',
                    'global!ace-u-max-height--full',
                    'ace-c-content-item--sidebar',
                ])}
            >
                <Panel
                    title={translateTab('panel_title.select_contract_partner')}
                    className={cx([
                        'ace-c-panel--full-bleed-content',
                        'global!ace-u-height--full',
                        'global!ace-u-max-height--full',
                    ])}
                >
                    <ScrollableBlock isLabelDisabled={true}>
                        <div className={cx('global!ace-u-flex')}>
                            <SearchBox
                                isComposedIn={true}
                                name="searchContractPartner"
                                placeholder={translateTab('search_box_placeholder.contract_partner_search')}
                                className={cx([
                                    'global!ace-u-flex--grow-1',
                                    'global!ace-u-margin--16-0',
                                ])}
                                isDropDownDisabled={true}
                                value={contractPartnerSearchQuery}
                                onChange={handleSearchQueryChange}
                                onSearchSubmit={handleContractPartnerSearch}
                                isDisabled={serviceCase.persistenceState === persistenceStates.PENDING
                                || serviceAssignment.persistenceState === persistenceStates.PENDING}
                            />
                        </div>
                        {!isSearchInProgress && !!contractPartnerRecommendations.length && (
                            <RadioButtonGroup
                                name="selectedContractPartnerId"
                                value={selectedContractPartnerId}
                                onChange={handleContractPartnerSelect}
                                isDisabled={serviceCase.persistenceState === persistenceStates.PENDING
                                || serviceAssignment.persistenceState === persistenceStates.PENDING}
                            >
                                <h4
                                    className={cx([
                                        'global!ace-u-typography--variant-body-bold',
                                        'global!ace-u-typography--align-left',
                                        'global!ace-u-margin--bottom-24',
                                    ])}
                                >
                                    {translateTab('heading.nearest_contract_partner')}
                                </h4>
                                {recommendedContractPartner ? (
                                    <ContractPartnerItem
                                        isDisabled={isPartnerChangingDisabled
                                        || serviceAssignment.listOfPreviousPartners?.includes(
                                            recommendedContractPartner.id,
                                        )}
                                        key={recommendedContractPartner.id}
                                        contractPartner={recommendedContractPartner}
                                        pickupLocationToTowingDestinationRoute={pickupLocationToTowingDestinationRoute}
                                    />
                                ) : (
                                    <div
                                        className={cx([
                                            'global!ace-u-typography--align-left',
                                            'global!ace-u-margin--bottom-24',
                                        ])}
                                    >
                                        {translateTab('text.nearest_contract_partner_unavailable')}
                                    </div>
                                )}
                                {!!serviceAssignment?.acePartner?.isManuallyAdded && (
                                    <h4
                                        className={cx([
                                            'global!ace-u-typography--variant-body-bold',
                                            'global!ace-u-typography--align-left',
                                            'global!ace-u-margin--bottom-24',
                                        ])}
                                    >
                                        {translateTab('heading.selected_partner')}
                                    </h4>
                                )}
                                {!!serviceAssignment?.acePartner?.isManuallyAdded && (
                                    <ContractPartnerItem
                                        // TODO: check if we need an ID
                                        key={serviceAssignment.acePartner.location.id}
                                        contractPartner={serviceAssignment.acePartner}
                                        isManuallyAdded={true}
                                        assignmentStatus={serviceAssignment.assignmentStatus}
                                        handleOnDelete={handleDeleteManuallyAddedContractPartner}
                                    />
                                )}
                                <h4
                                    className={cx([
                                        'global!ace-u-typography--variant-body-bold',
                                        'global!ace-u-typography--align-left',
                                        'global!ace-u-margin--bottom-24',
                                    ])}
                                >
                                    {translateTab('heading.other_contract_partners')}
                                </h4>
                                {contractPartnerRecommendations.map(contractPartner => {
                                    if (recommendedContractPartner
                                        && contractPartner.id === recommendedContractPartner.id) return null;

                                    return (
                                        <ContractPartnerItem
                                            key={contractPartner.id}
                                            contractPartner={contractPartner}
                                            isDisabled={isPartnerChangingDisabled
                                            || serviceAssignment.listOfPreviousPartners?.includes(contractPartner.id)
                                            }
                                            pickupLocationToTowingDestinationRoute={
                                                pickupLocationToTowingDestinationRoute
                                            }
                                        />
                                    );
                                })}
                            </RadioButtonGroup>
                        )}
                        {isSearchInProgress && (
                            <div
                                className={cx([
                                    'global!ace-u-padding--top-32',
                                    'global!ace-u-width--full',
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--align-center',
                                    'global!ace-u-flex--justify-center',
                                ])}
                            >
                                <LoaderSpinner />
                            </div>
                        )}
                        {!isSearchInProgress && (
                            <div
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--direction-row-reverse',
                                    'global!ace-u-flex--align-center',
                                    'global!ace-u-typography--variant-body-bold',
                                ])}
                            >
                                <ToggleSwitch
                                    name="toggleVBAContractPartnerSearch"
                                    value={true}
                                    isSelected={isVBASearchParam === 'false'
                                        ? null : isVBASearchActive}
                                    onChange={handleVBAContractPartnerSearch}
                                    isDisabled={!!serviceAssignment?.acePartner?.isManuallyAdded}
                                />
                                <p className={cx('global!ace-u-margin--right-8')}>
                                    {translateTab('toggle_label.show_vba_companies')}
                                </p>
                            </div>
                        )}
                    </ScrollableBlock>
                </Panel>
            </ContentItem>
        </ContentBlock>
    );
};

SAPServiceProviderTab.propTypes = {
    serviceCase: PropTypes.object,
    serviceAssignment: PropTypes.object,
    pickupLocationToTowingDestinationRoute: PropTypes.object,
    submitSAPContractPartnerForm: PropTypes.func.isRequired,
    searchSAPContractPartners: PropTypes.func.isRequired,
    initiateServiceAssignmentACEPartnerDeletionFlow: PropTypes.func.isRequired,
    initiateServiceAssignmentQMFeedbackDraftCreationFlow: PropTypes.func.isRequired,
    setActiveContractPartnerRecommendationsSnapshot: PropTypes.func.isRequired,
    history: PropTypes.object,
    contractPartners: PropTypes.object,
    contractPartnerRecommendationsPersistenceState: PropTypes.string.isRequired,
};

SAPServiceProviderTab.defaultProps = {
    serviceCase: null,
    serviceAssignment: null,
    pickupLocationToTowingDestinationRoute: null,
    history: null,
    contractPartners: null,
};

const mapStateToProps = (state, props) => {
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    const {getPickupLocationToTowingDestinationRoute} = serviceAssignmentSelectors;

    return {
        serviceCase: serviceCaseSelector(state, props),
        serviceAssignment: serviceAssignmentSelector(state, props),
        pickupLocationToTowingDestinationRoute: getPickupLocationToTowingDestinationRoute(state, props),
        contractPartners: state.contractPartners.contractPartners,
        contractPartnerRecommendationsPersistenceState: state.serviceAssignments.contractPartnerRecommendationsPersistenceState, // eslint-disable-line max-len
    };
};

const mapDispatchToProps = dispatch => ({
    submitSAPContractPartnerForm: payload => dispatch({
        type: sapActionTypes.SUBMIT_SAP_CONTRACT_PARTNER_FORM,
        payload,
    }),
    searchSAPContractPartners: payload => dispatch({
        type: sapActionTypes.SEARCH_SAP_CONTRACT_PARTNERS,
        payload,
    }),
    initiateServiceAssignmentACEPartnerDeletionFlow: payload => dispatch({
        type: serviceAssignmentActionTypes.INITIATE_SERVICE_ASSIGNMENT_ACE_PARTNER_DELETION_FLOW,
        payload,
    }),
    initiateServiceAssignmentQMFeedbackDraftCreationFlow: payload => dispatch({
        type: serviceAssignmentActionTypes.INITIATE_QM_FEEDBACK_DRAFT_CREATION_FLOW,
        payload,
    }),
    setActiveContractPartnerRecommendationsSnapshot: payload => dispatch({
        type: sapActionTypes.SET_SAP_ACTIVE_CONTRACT_PARTNER_RECOMMENDATIONS_SNAPSHOT,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SAPServiceProviderTab));
